import React, { useState, useEffect } from "react";
import { Table, Modal, Button, Typography, Space, List } from "antd";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebase";

const { Title, Text } = Typography;

const Refinement = () => {
  console.log("Refinement component is rendering");
  
  const [patients, setPatients] = useState([]);
  const [medecins, setMedecins] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [correctionModalVisible, setCorrectionModalVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // File name mappings based on correction.jsx
  const fileLabels = {
    img4: "Occlusale supérieure",
    img6: "Occlusale inférieure", 
    img7: "Intra-orale latérale droite",
    img8: "Intra-orale de face",
    img9: "Intra-orale latérale gauche",
    img12: "Maxillaire (Scan)",
    img13: "Mandibule (Scan)"
  };

  // File order: photos first, then scans
  const fileOrder = ['img4', 'img6', 'img7', 'img8', 'img9', 'img12', 'img13'];

  // Fetch medecins
  useEffect(() => {
    const fetchMedecins = async () => {
      try {
        const medecinsSnapshot = await getDocs(collection(db, "medecins"));
        const medecinsList = medecinsSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        setMedecins(medecinsList);
      } catch (error) {
        console.error("Error fetching medecins:", error);
      }
    };
    fetchMedecins();
  }, []);

  useEffect(() => {
    console.log("Refinement useEffect is running");
    
    const fetchPatients = async () => {
      try {
        console.log("Starting to fetch patients...");
        setLoading(true);
        setError(null);
        
        const patientsSnapshot = await getDocs(collection(db, "patients"));
        console.log("Patients snapshot received:", patientsSnapshot);
        
        const patientsList = patientsSnapshot.docs.map((doc) => ({
          key: doc.id,
          ...doc.data(),
        }));
        console.log("Fetched patients:", patientsList);
        console.log("Total patients:", patientsList.length);
        
        // Filter patients with corrections (same pattern as Eterna)
        const patientsWithCorrections = patientsList.filter(p => 
          Array.isArray(p.corrections) && p.corrections.length > 0
        );
        
        console.log("Patients with corrections:", patientsWithCorrections.length);
        console.log("Sample patient with corrections:", patientsWithCorrections[0]);
        
        setPatients(patientsWithCorrections);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching patients:", error);
        setError(error.message);
        setLoading(false);
      }
    };
    
    fetchPatients();
  }, []);

  const columns = [
    { title: "Patient ID", dataIndex: "key", key: "key", width: 120 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Surname", dataIndex: "surname", key: "surname" },
    {
      title: "Assigned Doctor",
      key: "assignedDoctor",
      render: (_, record) => {
        const medecin = medecins.find(m => m.uid === record.userId);
        return medecin ? medecin.name : 'N/A';
      }
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button type="primary" onClick={() => { setSelectedPatient(record); setModalVisible(true); }}>
          View Corrections
        </Button>
      ),
    },
  ];

  const showCorrectionDetails = (correction) => {
    setSelectedCorrection(correction);
    setCorrectionModalVisible(true);
  };

  const CorrectionsListModal = () => {
    if (!selectedPatient) return null;
    
    const corrections = [...(selectedPatient.corrections || [])].sort((a, b) => {
      // Try different date fields that might exist
      const dateA = a.requestDate || a.requestTimestamp || a.id?.replace('correction_', '') || '0';
      const dateB = b.requestDate || b.requestTimestamp || b.id?.replace('correction_', '') || '0';
      
      // Convert to timestamps for comparison
      const timestampA = new Date(dateA).getTime();
      const timestampB = new Date(dateB).getTime();
      
      return timestampB - timestampA; // Sort newest first
    });

    return (
      <Modal
        title={`Corrections - ${selectedPatient.name} ${selectedPatient.surname || ""}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <Text strong>Patient ID:</Text> {selectedPatient.key}
          </div>
          <div>
            <Text strong>Name:</Text> {selectedPatient.name} {selectedPatient.surname || ''}
          </div>
          
          <div style={{ marginTop: '20px' }}>
            <Title level={4}>Correction History</Title>
            <List
              dataSource={corrections}
              renderItem={(correction, idx) => {
                // Try to get the date from various possible fields
                let date = 'Unknown Date';
                if (correction.requestDate) {
                  date = new Date(correction.requestDate).toLocaleString();
                } else if (correction.requestTimestamp) {
                  date = new Date(correction.requestTimestamp).toLocaleString();
                } else if (correction.id) {
                  const timestamp = parseInt(correction.id.replace('correction_', ''));
                  if (!isNaN(timestamp)) {
                    date = new Date(timestamp).toLocaleString();
                  }
                }

                return (
                  <List.Item
                    key={idx}
                    style={{ 
                      padding: "12px", 
                      border: "1px solid #eee", 
                      borderRadius: "4px", 
                      marginBottom: "10px",
                      cursor: "pointer",
                      backgroundColor: "#fafafa"
                    }}
                    onClick={() => showCorrectionDetails(correction)}
                  >
                    <div style={{ width: "100%" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <Text strong>Date:</Text> {date}
                        </div>
                        <div>
                          <Button type="link" size="small">
                            View Files →
                          </Button>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          </div>
        </Space>
      </Modal>
    );
  };

  // Add download helper (from Medecins.jsx)
  const forceDownload = (url, filename) => {
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      })
      .catch(() => {
        // fallback: open in new tab
        window.open(url, '_blank');
      });
  };

  const CorrectionDetailsModal = () => {
    if (!selectedCorrection) return null;

    // Try to get the date from various possible fields
    let date = 'Unknown Date';
    if (selectedCorrection.requestDate) {
      date = new Date(selectedCorrection.requestDate).toLocaleString();
    } else if (selectedCorrection.requestTimestamp) {
      date = new Date(selectedCorrection.requestTimestamp).toLocaleString();
    } else if (selectedCorrection.id) {
      const timestamp = parseInt(selectedCorrection.id.replace('correction_', ''));
      if (!isNaN(timestamp)) {
        date = new Date(timestamp).toLocaleString();
      }
    }

    // Separate images and scans
    const images = [];
    const scans = [];
    if (selectedCorrection.images) {
      Object.entries(selectedCorrection.images).forEach(([key, url]) => {
        if (!url) return;
        if (["img4", "img6", "img7", "img8", "img9"].includes(key)) {
          images.push({ key, url });
        } else if (["img12", "img13"].includes(key)) {
          scans.push({ key, url });
        }
      });
    }

    return (
      <Modal
        title={`Correction Files - ${date}`}
        open={correctionModalVisible}
        onCancel={() => setCorrectionModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setCorrectionModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {/* Images Section */}
          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Photos</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {images.length > 0 ? images.map(({ key, url }) => (
                <div key={key} style={{ marginBottom: '16px' }}>
                  <img 
                    src={url} 
                    alt={fileLabels[key] || key} 
                    style={{ maxWidth: '120px', maxHeight: '120px', display: 'block', marginBottom: '8px' }} 
                    onClick={() => window.open(url, '_blank')}
                  />
                  <div style={{ fontSize: '12px', marginBottom: '4px' }}>{fileLabels[key] || key}</div>
                  <Button 
                    size="small" 
                    onClick={() => forceDownload(url, `${fileLabels[key] || key}.jpg`)}
                  >
                    Download
                  </Button>
                </div>
              )) : <Text type="secondary">No photos available</Text>}
            </div>
          </div>
          {/* Scans Section */}
          <div style={{ marginBottom: '24px' }}>
            <Title level={5}>Scans</Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {scans.length > 0 ? scans.map(({ key, url }) => (
                <div key={key}>
                  <Text strong>{fileLabels[key] || key}:</Text>
                  <Button 
                    size="small" 
                    style={{ marginLeft: '8px' }}
                    onClick={() => forceDownload(url, `${fileLabels[key] || key}.${url.toLowerCase().includes('.ply') ? 'ply' : 'stl'}`)}
                  >
                    Download
                  </Button>
                </div>
              )) : <Text type="secondary">No scans available</Text>}
            </div>
          </div>
        </Space>
      </Modal>
    );
  };

  console.log("About to render Refinement component with:", { patients, loading, error });

  return (
    <div>
      <Title level={3}>Refinement Patients</Title>
      
      {error && (
        <div style={{ color: 'red', marginBottom: '20px' }}>
          Error: {error}
        </div>
      )}
      
      {loading ? (
        <div>Loading patients...</div>
      ) : (
        <>
          <div style={{ marginBottom: '20px' }}>
            Found {patients.length} patients with corrections
          </div>
          <Table 
            dataSource={patients} 
            columns={columns} 
            rowKey="key" 
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <span>No patients with corrections found. If you expect to see data, check your Firestore and the 'corrections' field structure.</span> }}
          />
        </>
      )}
      
      <CorrectionsListModal />
      <CorrectionDetailsModal />
    </div>
  );
};

export default Refinement; 